import { CloseOutlined } from "@ant-design/icons";
import { Button, message, Popconfirm, Space, Table } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { LogMonitor } from "@/controls";
import ButtonReport from "@/controls/button-report";
import { isMobile } from "@/extensions/design";
import { fmtDate, fmtSyma } from "@/extensions/utils";
import { serverFetch } from "@/server";
import ReestrCnt from "./reestr-cnt";

const REESTR_MODAL_DEFAULT_WIDTH = 1100;
const REESTR_MODAL_MIN_WIDTH = 700;
const REESTR_MODAL_WIDTH_KEY = "reestrModalWidth";

const Reestr = ({ id, onClose, onRefresh }) => {
  const user = useSelector((s) => s.user);
  const [reestr, setReestr] = useState();
  const [cnts, setCnts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cntLoading, setCntLoading] = useState(false);
  const [editCntId, setEditCntId] = useState();
  const [modalWidth, setModalWidth] = useState(() => {
    const storedWidth = Number(localStorage.getItem(REESTR_MODAL_WIDTH_KEY));
    return Number.isFinite(storedWidth) && storedWidth > 0 ? storedWidth : REESTR_MODAL_DEFAULT_WIDTH;
  });
  const modalWidthRef = useRef(modalWidth);

  const isSuperadmin = user?.isSuperadmin;

  useEffect(() => {
    modalWidthRef.current = modalWidth;
  }, [modalWidth]);

  const getReestr = useCallback(() => {
    if (!id) return;

    setLoading(true);
    serverFetch(`Reestr/${id}`)
      .then(data => setReestr(data))
      .catch(e => {
        message.error(e.userMessage);
        onClose(false);
      })
      .finally(() => setLoading(false));
  }, [id, onClose]);

  const getCnts = useCallback(() => {
    if (!id) return;

    setCntLoading(true);
    serverFetch(`Reestr/CntList/${id}`)
      .then(data => setCnts(data))
      .catch(e => message.error(e.userMessage))
      .finally(() => setCntLoading(false));
  }, [id]);

  useEffect(() => {
    getReestr();
    getCnts();
  }, [getReestr, getCnts]);

  const refresh = () => {
    getReestr();
    getCnts();
    onRefresh();
  };

  const onSign = () => {
    serverFetch(`Reestr/Sign/${id}`, { method: "POST" })
      .then(data => {
        message.success("Реестр подписан");
        setReestr(data);
        onRefresh();
      })
      .catch(e => message.error(e.userMessage));
  };

  const onDeleteCnt = (cntId) => {
    serverFetch(`Reestr/Cnt/${cntId}`, { method: "DELETE" })
      .then(() => {
        message.success("Строка удалена");
        getCnts();
        getReestr();
        onRefresh();
      })
      .catch(e => message.error(e.userMessage));
  };

  const onRecalcCommission = () => {
    serverFetch(`Reestr/RecalcCommission/${id}`, { method: "POST" })
      .then(data => {
        message.success("Комиссия пересчитана");
        setReestr(data);
        getCnts();
        onRefresh();
      })
      .catch(e => message.error(e.userMessage));
  };

  const onCntClose = (refreshData = false) => {
    setEditCntId();
    if (refreshData) refresh();
  };

  const getModalWidth = (clientX) => {
    const maxWidth = Math.max(REESTR_MODAL_MIN_WIDTH, window.innerWidth - 16);
    return Math.min(Math.max(window.innerWidth - clientX, REESTR_MODAL_MIN_WIDTH), maxWidth);
  };

  const onResizeMouseDown = useCallback((e) => {
    if (isMobile) return;

    e.preventDefault();
    e.stopPropagation();
    document.body.classList.add("reestr-modal-resizing");

    const onMouseMove = (moveEvent) => {
      const nextWidth = Math.round(getModalWidth(moveEvent.clientX));
      modalWidthRef.current = nextWidth;
      setModalWidth(nextWidth);
    };

    const onMouseUp = () => {
      localStorage.setItem(REESTR_MODAL_WIDTH_KEY, String(modalWidthRef.current));
      document.body.classList.remove("reestr-modal-resizing");
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const cntColumns = [
    ...(isSuperadmin && !reestr?.signed ? [{
      title: "",
      key: "actions",
      width: 36,
      render: (_, record) => (
        <div className="reestr-cnt-row-actions" onClick={e => e.stopPropagation()}>
          <Popconfirm title="Удалить строку?" onConfirm={() => onDeleteCnt(record.id)}>
            <img src="/images/trashcan.svg" alt="Удалить" title="Удалить" className="sport-btn-icon" />
          </Popconfirm>
        </div>
      ),
    }] : []),
    { title: "#", dataIndex: "id", width: 60 },
    { title: "Платеж #", dataIndex: "paymentId", width: 90 },
    { title: "Вид", dataIndex: "vid", width: 90, render: value => value === 2 ? "Возврат" : "Оплата" },
    { title: "Дата", dataIndex: "paymentDocDate", width: 110, render: fmtDate },
    { title: "Вид оплаты", dataIndex: "paymentVidName", width: 140 },
    { title: "Клиент", dataIndex: "clientName", width: 180 },
    { title: "Услуга - основание", dataIndex: "productName", width: 220 },
    { title: "Платеж", dataIndex: "paymentSyma", width: 120, align: "right", render: value => value == null ? "" : fmtSyma(value) },
    { title: "Сумма", dataIndex: "syma", width: 100, align: "right", render: fmtSyma },
    { title: "Комментарий", dataIndex: "comment", width: 150 },
  ];

  return (
    <div className="sport-modal">
      <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose(false)}>
        <div className={isMobile ? "sport-modal-content mobile-content" : "sport-modal-content reestr-modal-content"} style={isMobile ? {} : { width: modalWidth }} onMouseDown={e => e.stopPropagation()}>
          {!isMobile && <div className="reestr-modal-resize-handle" onMouseDown={onResizeMouseDown} />}
          <div className="reestr-modal-title">
            <span>Реестр #{id}</span>
            <CloseOutlined className="sport-section-title-close" onClick={() => onClose(false)} style={{ zIndex: 9 }} />
          </div>
          <div className="reestr-modal-body">
            <Space className="reestr-modal-toolbar">
              {!isSuperadmin && user?.isAdmin && reestr && !reestr.signed && (
                <Popconfirm title="Подписать реестр?" onConfirm={onSign}>
                  <Button type="primary">Подписать</Button>
                </Popconfirm>
              )}
              {isSuperadmin && reestr && !reestr.signed && <Button onClick={() => setEditCntId(0)}>Добавить</Button>}
              {isSuperadmin && reestr && !reestr.signed && (
                <Popconfirm title="Пересчитать комиссию по реестру?" onConfirm={onRecalcCommission}>
                  <Button>Пересчет</Button>
                </Popconfirm>
              )}
              {reestr && <ButtonReport id={id} vid={8} begPer={reestr.docDate} endPer={reestr.docDate} />}
              <LogMonitor vid={20} id={id} />
            </Space>
            <Table className="reestr-cnts-table" dataSource={cnts} columns={cntColumns} rowKey="id" loading={loading || cntLoading} pagination={false} size="small" scroll={{ x: 1220, y: "100%" }} rowClassName={record => [isSuperadmin && !reestr?.signed ? "reestr-cnt-editable-row" : "", record.vid === 2 ? "reestr-cnt-return-row" : ""].filter(Boolean).join(" ")} onRow={record => isSuperadmin && !reestr?.signed ? { onClick: () => setEditCntId(record.id) } : {}} />
          </div>
        </div>
      </div>

      {editCntId !== undefined && <ReestrCnt reestrId={id} id={editCntId} onClose={onCntClose} />}
    </div>
  );
};

export default Reestr;